import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  ShieldCheck, Upload, FileText, Download, Trash2, LogOut, ArrowLeft,
  Inbox, CalendarClock, FolderLock, Loader2, MessageCircle, CheckCircle2, RefreshCw,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z" />
    <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z" />
    <path fill="#EA4335" d="M12 4.75c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 1.46 14.97.5 12 .5A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 6.68 9.14 4.75 12 4.75Z" />
  </svg>
);

const fmtSize = (b) => {
  if (!b) return '';
  const mb = b / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${(b / 1024).toFixed(0)} KB`;
};
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '');

export default function Portal() {
  const { user, loading, login, logout, checkAuth } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';

  const [tab, setTab] = useState('documents');
  const [docs, setDocs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [consults, setConsults] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [wa, setWa] = useState({ connected: false, qr: null, loading: true });

  useEffect(() => {
    if (!user && !loading && !window.location.hash.includes('session_id=')) {
      checkAuth();
    }
  }, [user, loading, checkAuth]);

  const loadDocs = useCallback(async () => {
    try {
      const res = await api.get('/documents');
      setDocs(res.data);
    } catch (e) { /* ignore */ }
  }, []);

  const loadAdmin = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const [c, s] = await Promise.all([api.get('/contact'), api.get('/consultations')]);
      setContacts(c.data);
      setConsults(s.data);
    } catch (e) { /* ignore */ }
  }, [isAdmin]);

  useEffect(() => {
    if (user) { loadDocs(); loadAdmin(); }
  }, [user, loadDocs, loadAdmin]);

  const loadWa = useCallback(async () => {
    try {
      const res = await api.get('/whatsapp/qr');
      setWa({ connected: !!res.data.connected, qr: res.data.qr || null, loading: false });
    } catch (e) {
      setWa({ connected: false, qr: null, loading: false });
    }
  }, []);

  useEffect(() => {
    if (!isAdmin || tab !== 'whatsapp') return;
    loadWa();
    const id = setInterval(loadWa, 4000);
    return () => clearInterval(id);
  }, [isAdmin, tab, loadWa]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post('/documents/upload', fd);
      toast.success('Document uploaded securely.');
      await loadDocs();
    } catch (err) {
      toast.error('Upload failed. Please try again.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const onDownload = async (doc) => {
    setBusy(true);
    try {
      const res = await api.get(`/documents/${doc.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (err) {
      toast.error('Could not open file.');
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (doc) => {
    try {
      await api.delete(`/documents/${doc.id}`);
      toast.success('Document removed.');
      setDocs((d) => d.filter((x) => x.id !== doc.id));
    } catch (err) {
      toast.error('Could not remove file.');
    }
  };

  // ---------- Loading ----------
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ink-900">
        <Loader2 className="w-8 h-8 text-gold-light animate-spin" />
      </div>
    );
  }

  // ---------- Not signed in ----------
  if (!user) {
    return (
      <div className="min-h-screen bg-ink-900 flex items-center justify-center px-6">
        <div className="absolute top-6 left-6">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm" data-testid="portal-back-home">
            <ArrowLeft className="w-4 h-4" /> Back to site
          </button>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-md w-full bg-ink-800/80 backdrop-blur-2xl border border-white/10 rounded-3xl p-10 text-center shadow-2xl"
          data-testid="portal-login-card"
        >
          <div className="w-16 h-16 rounded-2xl bg-gold/10 border border-gold/30 flex items-center justify-center mx-auto text-gold-light mb-6">
            <FolderLock className="w-8 h-8" strokeWidth={1.5} />
          </div>
          <h1 className="font-display text-3xl text-white">Client Portal</h1>
          <p className="text-slate-400 text-sm mt-3 mb-8">Sign in to securely upload tax documents and track your compliance in one place.</p>
          <button
            onClick={login}
            data-testid="google-signin-btn"
            className="w-full flex items-center justify-center gap-3 py-3.5 rounded-full bg-white text-ink-900 font-semibold text-sm hover:bg-slate-100 transition-colors"
          >
            <GoogleIcon /> Continue with Google
          </button>
          <p className="text-xs text-slate-600 mt-6 flex items-center justify-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5" /> 256-bit encrypted & confidential
          </p>
        </motion.div>
      </div>
    );
  }

  // ---------- Dashboard ----------
  const tabs = isAdmin
    ? [['documents', 'All Documents', FileText], ['enquiries', 'Enquiries', Inbox], ['consultations', 'Bookings', CalendarClock], ['whatsapp', 'WhatsApp', MessageCircle]]
    : [['documents', 'My Documents', FileText]];

  return (
    <div className="min-h-screen bg-ink-900">
      {/* Top bar */}
      <header className="border-b border-white/5 bg-ink-800/60 backdrop-blur-xl px-6 lg:px-10 py-4 flex items-center justify-between sticky top-0 z-40">
        <button onClick={() => navigate('/')} className="flex items-center gap-3" data-testid="portal-logo">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-gold-dark to-gold-light flex items-center justify-center text-ink-900 font-display font-bold">SN</div>
          <span className="font-display text-lg text-white hidden sm:block">Client Portal</span>
        </button>
        <div className="flex items-center gap-4">
          {user.picture && <img src={user.picture} alt="" className="w-9 h-9 rounded-full border border-gold/40" />}
          <div className="text-right hidden sm:block">
            <p className="text-sm text-white font-medium leading-none">{user.name}</p>
            <p className="text-xs text-gold-light mt-1">{isAdmin ? 'Administrator' : 'Client'}</p>
          </div>
          <button onClick={async () => { await logout(); navigate('/'); }} data-testid="portal-logout-btn" className="flex items-center gap-2 px-4 py-2 rounded-full bg-ink-700 hover:bg-ink-800 border border-slate-700 text-xs text-slate-300">
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
        <div className="mb-8">
          <h1 className="font-display text-3xl sm:text-4xl text-white">Welcome, {user.name?.split(' ')[0]}</h1>
          <p className="text-slate-400 mt-2">{isAdmin ? 'Manage documents, enquiries and consultation bookings.' : 'Upload and manage your tax documents securely.'}</p>
        </div>

        {/* Tabs */}
        {tabs.length > 1 && (
          <div className="flex gap-3 mb-8 flex-wrap">
            {tabs.map(([id, label, Icon]) => (
              <button key={id} onClick={() => setTab(id)} data-testid={`portal-tab-${id}`} className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-colors ${tab === id ? 'bg-gold text-ink-900' : 'bg-ink-800 text-slate-300 border border-slate-800 hover:bg-ink-700'}`}>
                <Icon className="w-4 h-4" strokeWidth={1.5} /> {label}
              </button>
            ))}
          </div>
        )}

        {/* Documents tab */}
        {tab === 'documents' && (
          <div className="space-y-6">
            {!isAdmin && (
              <label className="block bg-ink-800 border border-dashed border-slate-700 hover:border-gold/50 rounded-2xl p-8 text-center cursor-pointer transition-colors" data-testid="upload-dropzone">
                <input type="file" className="hidden" onChange={onUpload} data-testid="file-input" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx,.csv" />
                <div className="w-14 h-14 rounded-2xl bg-gold/10 border border-gold/30 flex items-center justify-center mx-auto text-gold-light mb-4">
                  {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <Upload className="w-6 h-6" strokeWidth={1.5} />}
                </div>
                <p className="text-white font-medium">{uploading ? 'Uploading…' : 'Click to upload a document'}</p>
                <p className="text-xs text-slate-500 mt-1">PDF, images or office files · 256-bit encrypted</p>
              </label>
            )}

            <div className="bg-ink-800 border border-slate-800 rounded-2xl overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between">
                <h3 className="text-white font-medium text-sm">{isAdmin ? 'All Client Documents' : 'Your Document Vault'}</h3>
                <span className="text-xs text-slate-500">{docs.length} file{docs.length !== 1 ? 's' : ''}</span>
              </div>
              {docs.length === 0 ? (
                <p className="text-slate-500 text-sm text-center py-12" data-testid="docs-empty">No documents yet.</p>
              ) : (
                <ul className="divide-y divide-slate-800">
                  {docs.map((d) => (
                    <li key={d.id} className="flex items-center justify-between gap-4 px-6 py-4 hover:bg-ink-700/40 transition-colors" data-testid={`doc-row-${d.id}`}>
                      <div className="flex items-center gap-4 min-w-0">
                        <div className="w-10 h-10 rounded-xl bg-ink-900 border border-slate-800 flex items-center justify-center text-gold-light shrink-0">
                          <FileText className="w-5 h-5" strokeWidth={1.5} />
                        </div>
                        <div className="min-w-0">
                          <p className="text-white text-sm font-medium truncate">{d.original_filename}</p>
                          <p className="text-xs text-slate-500 truncate">
                            {fmtSize(d.size)} · {fmtDate(d.created_at)}{isAdmin && d.owner_email ? ` · ${d.owner_email}` : ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button onClick={() => onDownload(d)} disabled={busy} data-testid={`doc-download-${d.id}`} className="p-2 rounded-lg bg-ink-900 border border-slate-700 text-slate-300 hover:text-gold-light" title="Download">
                          <Download className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                        <button onClick={() => onDelete(d)} data-testid={`doc-delete-${d.id}`} className="p-2 rounded-lg bg-ink-900 border border-slate-700 text-slate-300 hover:text-red-400" title="Remove">
                          <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {/* Enquiries tab (admin) */}
        {tab === 'enquiries' && isAdmin && (
          <div className="bg-ink-800 border border-slate-800 rounded-2xl overflow-hidden" data-testid="enquiries-panel">
            <div className="px-6 py-4 border-b border-slate-800"><h3 className="text-white font-medium text-sm">Contact Enquiries</h3></div>
            {contacts.length === 0 ? (
              <p className="text-slate-500 text-sm text-center py-12">No enquiries yet.</p>
            ) : (
              <ul className="divide-y divide-slate-800">
                {contacts.map((c) => (
                  <li key={c.id} className="px-6 py-4">
                    <div className="flex items-center justify-between gap-4">
                      <p className="text-white text-sm font-medium">{c.name} <span className="text-gold-light font-normal">· {c.email}</span></p>
                      <span className="text-xs text-slate-500">{fmtDate(c.created_at)}</span>
                    </div>
                    <p className="text-slate-400 text-sm mt-1.5">{c.message}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Consultations tab (admin) */}
        {tab === 'consultations' && isAdmin && (
          <div className="bg-ink-800 border border-slate-800 rounded-2xl overflow-hidden" data-testid="consultations-panel">
            <div className="px-6 py-4 border-b border-slate-800"><h3 className="text-white font-medium text-sm">Consultation Bookings</h3></div>
            {consults.length === 0 ? (
              <p className="text-slate-500 text-sm text-center py-12">No bookings yet.</p>
            ) : (
              <ul className="divide-y divide-slate-800">
                {consults.map((c) => (
                  <li key={c.id} className="px-6 py-4">
                    <div className="flex items-center justify-between gap-4">
                      <p className="text-white text-sm font-medium">{c.name} <span className="text-gold-light font-normal">· {c.service}</span></p>
                      <span className="text-xs text-slate-500">{c.date} · {c.time}</span>
                    </div>
                    <p className="text-slate-400 text-xs mt-1.5">{c.email}{c.phone ? ` · ${c.phone}` : ''}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        {/* WhatsApp tab (admin) */}
        {tab === 'whatsapp' && isAdmin && (
          <div className="bg-ink-800 border border-slate-800 rounded-2xl p-8" data-testid="whatsapp-panel">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-11 h-11 rounded-xl bg-[#25D366]/15 border border-[#25D366]/30 flex items-center justify-center text-[#25D366]">
                <MessageCircle className="w-5 h-5" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="text-white font-medium">WhatsApp Notifications</h3>
                <p className="text-xs text-slate-400">Get an instant WhatsApp on +91 99127 40286 for every enquiry & booking.</p>
              </div>
            </div>

            {wa.loading ? (
              <div className="flex items-center gap-2 text-slate-400 text-sm py-8 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Checking connection…</div>
            ) : wa.connected ? (
              <div className="text-center py-8" data-testid="whatsapp-connected">
                <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mx-auto text-emerald-400 mb-4">
                  <CheckCircle2 className="w-8 h-8" strokeWidth={1.5} />
                </div>
                <p className="text-white font-medium">WhatsApp connected</p>
                <p className="text-sm text-slate-400 mt-1">Notifications are active. New submissions will arrive on your WhatsApp.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 items-center" data-testid="whatsapp-qr-panel">
                <div className="bg-white rounded-2xl p-4 flex items-center justify-center min-h-[240px]">
                  {wa.qr ? (
                    <img src={wa.qr} alt="WhatsApp QR" className="w-full max-w-[240px]" data-testid="whatsapp-qr-img" />
                  ) : (
                    <div className="flex flex-col items-center gap-2 text-slate-500 text-sm"><RefreshCw className="w-5 h-5 animate-spin" /> Generating QR…</div>
                  )}
                </div>
                <div className="space-y-3 text-sm text-slate-300">
                  <p className="text-white font-medium">Link your WhatsApp (one time)</p>
                  <ol className="space-y-2 list-decimal list-inside text-slate-400">
                    <li>Open WhatsApp on <b className="text-slate-200">+91 99127 40286</b></li>
                    <li>Tap <b className="text-slate-200">Settings → Linked Devices</b></li>
                    <li>Tap <b className="text-slate-200">Link a Device</b></li>
                    <li>Scan the QR code on the left</li>
                  </ol>
                  <p className="text-xs text-slate-500 pt-2">The QR refreshes automatically. Once linked, this screen will show “connected”.</p>
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
